import React, { useState, useEffect } from "react";
import { Store } from "../../../__generated__/graphql";
import { HiArrowDownTray } from "react-icons/hi2";
import { useMutation } from "@apollo/client";
import { notify, notifyError } from "../../../utils/toast";
import { ISSUE_STORE_COUPON } from "../api/issue-store-coupon";
import BottomSheet from "../../../components/common/bottom-sheet";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import type {
  IssueStoreCouponMutation,
  IssueStoreCouponMutationVariables,
} from "../../../__generated__/graphql";

interface StoreCouponSectionProps {
  store: Store | undefined;
  onWebCTA?: () => void;
  isLoggedIn?: boolean;
  onLoginCTA?: () => void;
  /** store.isLiked 값을 그대로 전달. 조건부 쿠폰 모달 표시 여부 판단에 사용. */
  isLiked?: boolean | null;
  /** autoFulfillConditions 성공 후 호출 - 매장 데이터 refetch로 isLiked 즉시 반영 */
  onLikeStateChanged?: () => void;
}

const StoreCouponSection: React.FC<StoreCouponSectionProps> = ({
  store,
  isLoggedIn,
  onLoginCTA,
  isLiked,
  onLikeStateChanged,
}) => {
  const coupon = store?.availableStoreCoupon;
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [showLikeModal, setShowLikeModal] = useState(false);

  const [issueStoreCoupon, { loading: isIssuing }] = useMutation<
    IssueStoreCouponMutation,
    IssueStoreCouponMutationVariables
  >(ISSUE_STORE_COUPON, {
    onCompleted: (data, clientOptions) => {
      if (data.issueStoreCoupon.ok) {
        notify("쿠폰이 발급되었습니다!");
        // autoFulfillConditions로 좋아요가 생성된 경우 즉시 반영
        if (clientOptions?.variables?.autoFulfillConditions) {
          onLikeStateChanged?.();
        }
      } else {
        notifyError(
          data.issueStoreCoupon.error || "쿠폰 발급에 실패했습니다."
        );
      }
    },
    onError: (error) => {
      notifyError(error.message || "쿠폰 발급 중 오류가 발생했습니다.");
    },
  });

  useEffect(() => {
    if (!coupon?.endAt) {
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const endDate = new Date(coupon.endAt!);
      const difference = endDate.getTime() - now.getTime();
      const oneDayInMs = 1000 * 60 * 60 * 24;

      if (difference <= 0) {
        setTimeLeft("만료됨");
        return;
      }

      // 24시간이 넘으면 시간 표기하지 않음
      if (difference > oneDayInMs) {
        setTimeLeft("");
        return;
      }

      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [coupon?.endAt]);

  if (!coupon) {
    return null;
  }

  const remainingCount =
    coupon.maxUsageQuantity !== null && coupon.maxUsageQuantity !== undefined
      ? coupon.maxUsageQuantity - (coupon.usageCount || 0)
      : null;

  const getCouponStatus = () => {
    if (coupon.isExpired) {
      return { text: "만료됨", className: "text-gray-500" };
    }
    if (coupon.isSoldOut || remainingCount === 0) {
      return { text: "선착순 마감", className: "text-red-500" };
    }
    return { text: "사용 가능", className: "text-green-600" };
  };

  const status = getCouponStatus();

  /** 이 쿠폰이 알림받기 조건을 가지고 있는지 */
  const hasLikeRequiredCondition = coupon.conditions?.some(
    (c) => c.type === "LIKE_REQUIRED"
  ) ?? false;

  const handleIssueCoupon = () => {
    if (!isLoggedIn) {
      onLoginCTA?.();
      return;
    }

    if (!coupon?.id) {
      notifyError("쿠폰 정보를 찾을 수 없습니다.");
      return;
    }

    if (isIssuing) return;

    // 알림받기 조건이 있는데 아직 좋아요를 안 한 경우 → 모달 표시
    if (hasLikeRequiredCondition && !isLiked) {
      setShowLikeModal(true);
      return;
    }

    issueStoreCoupon({
      variables: { couponId: coupon.id },
    });
  };

  /** 모달에서 "예" 선택: 알림받기 + 쿠폰 발급 동시 처리 */
  const handleConfirmWithLike = () => {
    setShowLikeModal(false);
    if (!coupon?.id) return;
    issueStoreCoupon({
      variables: { couponId: coupon.id, autoFulfillConditions: true },
    });
  };

  return (
    <>
      <div className="w-full px-5 py-2 mt-2  rounded-xl">
        <div className="w-full rounded-xl bg-gradient-to-br from-[#1562fc] to-[#58a6ff] p-3 shadow-lg">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-1">
                {coupon.title}
              </h3>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xl font-bold text-white">
                  {coupon.discountValue?.toLocaleString() || 0}원
                </span>
                <span className="text-white/90 text-xs">할인</span>
              </div>
              {hasLikeRequiredCondition && (
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-white/80 text-xs">🔔 알림받기 고객 전용</span>
                </div>
              )}
            </div>
            <div
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${status.className} bg-white/80 backdrop-blur-sm`}
            >
              {status.text}
            </div>
          </div>

          <div className="border-t border-white/20 pt-2 mt-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-white/90 text-md">
                  {remainingCount !== null && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/70">남은 수량:</span>
                      <span className="font-semibold">
                        {remainingCount > 0
                          ? `${remainingCount}개`
                          : "선착순 마감되었습니다"}
                      </span>
                    </div>
                  )}
                  {remainingCount !== null && remainingCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/80">(사용순)</span>
                    </div>
                  )}
                </div>
                {coupon.endAt && timeLeft && (
                  <div className="text-white/90 text-xl font-semibold">
                    {timeLeft}
                  </div>
                )}
              </div>
              <button
                onClick={handleIssueCoupon}
                disabled={
                  isIssuing ||
                  coupon.isExpired === true ||
                  coupon.isSoldOut === true ||
                  remainingCount === 0
                }
                className="p-2 bg-white text-[#1562fc] rounded-lg hover:bg-white/90 transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <HiArrowDownTray className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 알림받기 조건 안내 바텀시트 */}
      <BottomSheet isOpen={showLikeModal} onClose={() => setShowLikeModal(false)}>
        <div className="flex flex-col items-center">
          <FadeInWrapper className="mb-2 text-lg font-bold text-center">
            🔔 알림받기가 필요한 쿠폰이에요
          </FadeInWrapper>
          <FadeInWrapper className="mb-6 text-center text-gray-500 text-sm leading-relaxed whitespace-pre-line">
            {"이 매장에 알림받기를 한 사람에게만\n발급이 가능한 쿠폰입니다.\n\n알림받기를 하고 쿠폰을 발급할까요?"}
          </FadeInWrapper>
          <FadeInWrapper className="flex justify-center gap-2 w-full">
            <button
              type="button"
              onClick={() => setShowLikeModal(false)}
              className="w-1/3 px-4 py-3 bg-gray-200 rounded-3xl font-bold"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleConfirmWithLike}
              disabled={isIssuing}
              className="w-2/3 px-4 py-3 bg-[#1562fc] text-white rounded-3xl font-bold disabled:opacity-50"
            >
              {isIssuing ? "처리 중..." : "알림받기 + 발급"}
            </button>
          </FadeInWrapper>
        </div>
      </BottomSheet>
    </>
  );
};

export default StoreCouponSection;
